// บัตรงาน: รอใคร/รออะไร/วันติดตาม · ผลหลังปิดงาน (จาก billingType เดิม) · เช็กลิสต์พร้อมส่งช่าง + คำเตือนก่อนนัดหมาย
import { bangkokDate } from './time.js';
import { billingDocKind } from './job-rules.js';
// ---------------------------------------------------------------- B1 รอใคร / รออะไร / วันติดตาม
export const WAITING_ON = ['customer', 'owner', 'sales', 'purchasing', 'technician', 'admin'];
export const WAITING_ON_INFO = {
    customer: { label: 'ลูกค้า' },
    owner: { label: 'คุณฟลุ๊ค' },
    sales: { label: 'ฝ่ายขาย' },
    purchasing: { label: 'จัดซื้อ' },
    technician: { label: 'ช่าง' },
    admin: { label: 'แอดมิน' },
};
export function isWaitingOn(v) {
    return typeof v === 'string' && WAITING_ON.includes(v);
}
/** วันติดตาม (YYYY-MM-DD ตามปฏิทินไทย) เทียบกับวันนี้ (ไทย) */
export function followUpState(followUpDate, now) {
    if (!followUpDate)
        return 'none';
    const today = bangkokDate(now);
    if (followUpDate < today)
        return 'overdue';
    return followUpDate === today ? 'today' : 'upcoming';
}
// ---------------------------------------------------------------- B2 ผลหลังปิดงาน (ไม่มีคอลัมน์ใหม่ — คำนวณจาก billingType/billingRef/ใบเสนอราคา)
export const POST_CLOSE_OUTCOMES = ['quotation', 'warranty_ma', 'internal'];
export const POST_CLOSE_OUTCOME_INFO = {
    quotation: { label: 'เสนอราคา/คิดเงิน' },
    warranty_ma: { label: 'ประกัน/MA' },
    internal: { label: 'งานภายใน' },
};
/**
 * ผลหลังปิดงาน 3 แบบ จาก billingType ที่ช่าง/แอดมินเลือกตอนส่งแอดมิน (ไม่เก็บซ้ำ):
 * charge → quotation · warranty/contract → warranty_ma · internal → internal · ยังไม่เลือก → null
 * เลข SETQ = เลข PEAK ของใบเสนอราคาที่ผูกกับงาน (ใบล่าสุดที่ไม่ void) หรือเลขเอกสารที่แอดมินพิมพ์ ถ้าเป็นใบเสนอราคา
 */
export function postCloseOutcome(job) {
    const map = { charge: 'quotation', warranty: 'warranty_ma', contract: 'warranty_ma', internal: 'internal' };
    const outcome = job.billingType && job.billingType in map ? map[job.billingType] : null;
    const linked = [...(job.quotations ?? [])]
        .filter((q) => q.status !== 'void' && q.peakQuotationNo?.trim())
        .sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime())[0];
    const ref = job.billingRef?.trim();
    const setqNo = linked?.peakQuotationNo?.trim() ?? (ref && billingDocKind(ref) === 'quotation' ? ref : null);
    return { outcome, setqNo };
}
// ---------------------------------------------------------------- B3 พร้อมส่งช่าง (ก่อนเปลี่ยนเป็น "นัดหมาย")
/** 7 ช่อง จาก learning/แอดมิน.md หัวข้อ 1 (FTFR) — รูปหน้างานไม่บังคับ */
export const DISPATCH_CHECK_ITEMS = [
    { key: 'contact', label: 'ผู้ติดต่อ + เบอร์หน้างาน' },
    { key: 'address', label: 'ที่อยู่ + GPS' },
    { key: 'scope', label: 'อาการ หรือขอบเขตงาน' },
    { key: 'site_photo', label: 'รูปหน้างาน (ถ้ามี)', optional: true },
    { key: 'equipment', label: 'อุปกรณ์พร้อม + ใครเตรียม' },
    { key: 'access', label: 'ข้อจำกัดเข้าพื้นที่ (เวลา, ลูกค้าอยู่หรือไม่)' },
    { key: 'skill', label: 'ช่างที่มีทักษะตรงงาน' },
];
export const DISPATCH_CHECK_KEYS = DISPATCH_CHECK_ITEMS.map((i) => i.key);
/** คำเตือนก่อนนัดหมาย — ไม่บล็อก (ผู้ใช้ยืนยันแล้วไปต่อได้) */
export function dispatchWarnings(ctx) {
    const out = [];
    const items = ctx.check?.items ?? {};
    const missing = DISPATCH_CHECK_ITEMS.filter((i) => !('optional' in i && i.optional) && items[i.key] !== true);
    if (missing.length)
        out.push({ code: 'dispatch_check', message: `เช็กลิสต์พร้อมส่งช่างยังไม่ครบ: ${missing.map((i) => i.label).join(', ')}` });
    if (items.equipment === true && !ctx.check?.equipmentBy?.trim())
        out.push({ code: 'equipment_by', message: 'ยังไม่ระบุว่าใครเตรียมอุปกรณ์' });
    if (ctx.visitTechnicians === null)
        out.push({ code: 'no_visit', message: 'ยังไม่มีรอบนัด (วัน/เวลา/ช่าง)' });
    else if (ctx.visitTechnicians.length === 0)
        out.push({ code: 'no_technician', message: 'รอบนัดยังไม่ได้ระบุช่าง' });
    else if (ctx.responsible && !ctx.visitTechnicians.some((t) => t.id === ctx.responsible.id)) {
        out.push({
            code: 'technician_not_responsible',
            message: `ช่างในนัด (${ctx.visitTechnicians.map((t) => t.name).join(', ')}) ไม่ใช่ผู้รับผิดชอบงาน (${ctx.responsible.name}) — MR.SETCOM จะเตือนผิดคน`,
        });
    }
    return out;
}
// ---------------------------------------------------------------- B4 เตือนช่างซ้ำ / เปลี่ยนช่างใกล้เวลานัด
/** กดส่งเตือนช่างซ้ำได้ 1 ครั้งต่อรอบนัดต่อ 5 นาที */
export const VISIT_REMIND_COOLDOWN_MS = 5 * 60_000;
/** เปลี่ยนช่างเมื่อนัดจะเริ่มภายใน 30 นาที (หรือเลยเวลาแล้ว) → เตือนช่างใหม่ทันที */
export const TECH_CHANGE_URGENT_MS = 30 * 60_000;
export function isUrgentTechChange(scheduledStart, now) {
    return scheduledStart.getTime() - now.getTime() < TECH_CHANGE_URGENT_MS;
}
/** มีเนื้อหาให้แสดงไหม (ว่างทุกช่อง = ไม่แนบการ์ด) */
export function hasProblemSummary(s) {
    if (!s)
        return false;
    return !!s.cause?.trim() || !!s.notes?.trim() || !!s.fixSteps?.some((x) => x.trim());
}
//# sourceMappingURL=ticket-followup.js.map